import { inject, Injectable } from '@angular/core';
import { CanActivate, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { UserInfoService } from '../services/user-info.service';
import { UserInfoHeader } from '../models/user-info.model';
import { DsToastService, DsToastModel } from '@bmw-ds/components';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {

    private authService = inject(AuthService);
    private userInfoService = inject(UserInfoService);
    private toastService = inject(DsToastService);

    canActivate(): Observable<boolean | UrlTree> {
        const accessToken = this.authService.getAccessToken();

        if (!accessToken) {

            const hadPreviousSession = !!sessionStorage.getItem('redirectHandled');

            if (hadPreviousSession) {
                this.addToast('critical', 'Your session has expired. Please log in again.');
                this.userInfoService.clearIdentity();
                sessionStorage.removeItem('redirectHandled');
            }

            this.authService.handleRedirect();
            return of(false);
        }

        // Validate token first, then hydrate user info/cache from the validated identity.
        return this.userInfoService.getUserInfo().pipe(
            switchMap(userInfo => {
                if (!userInfo?.sub) {
                    return of(false);
                }

                this.userInfoService.setIdentity(userInfo);

                return this.userInfoService.getFullNamesRFC({ iv_user: userInfo.sub.toUpperCase() }).pipe(
                    tap(res => this.userInfoService.userInfo.set(this.mapToHeader(res.es_userinfo))),
                    map(() => true),
                    catchError(() => {
                        this.userInfoService.userInfo.set(this.mapToHeader(userInfo));
                        return of(true);
                    })
                );
            }),
            catchError(() => {
                this.userInfoService.clearIdentity();
                this.authService.handleRefreshRedirect();
                return of(false);
            })
        );
    }

    private mapToHeader(user: any): UserInfoHeader {
        return {
            username: `${user.name1 || user.name || 'Unknown'} ${user.name2 || ''}`.trim(),
            department: user.department || user.abtlg || '',
            profileImg: user.profileImg || '',
        };
    }

    private addToast(tone: 'critical' | 'positive' | 'neutral', toastText: string): void {
        this.toastService.pushToast({
            id: 'session-error-toast',
            tone: tone,
            toastText: toastText,
        } as DsToastModel);
    }

}
